import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
} from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import CopyField from '~/components/Fields/CopyField';
import { OptimizationObjectiveRow } from '~/state/atoms/dose/optimizationObjectivesAtom';
import { tableScrollbarStyles } from '../tableScrollbarStyles';
import ObjectivesTableHead, { COL_COUNT } from './ObjectivesTableHead';

type Order = 'asc' | 'desc';

type RowGroup = {
    structure: string;
    roiGroupIndex: number;
    visible: boolean;
    rows: OptimizationObjectiveRow[];
};

function buildGroups(
    rows: OptimizationObjectiveRow[],
    order: Order
): RowGroup[] {
    const groupMap = new Map<number, RowGroup>();
    const groupOrder: number[] = [];
    rows.forEach((row) => {
        if (!groupMap.has(row.roiGroupIndex)) {
            groupMap.set(row.roiGroupIndex, {
                structure: row.structure,
                roiGroupIndex: row.roiGroupIndex,
                visible: row.visible,
                rows: [],
            });
            groupOrder.push(row.roiGroupIndex);
        }
        groupMap.get(row.roiGroupIndex)!.rows.push(row);
    });

    const groups = groupOrder.map((i) => groupMap.get(i)!);

    groups.sort((a, b) => {
        const visDiff = (b.visible ? 1 : 0) - (a.visible ? 1 : 0);
        if (visDiff !== 0) {
            return visDiff;
        }
        const cmp = a.structure.localeCompare(b.structure);
        return order === 'asc' ? cmp : -cmp;
    });

    return groups;
}

const TYPE_STYLE: Record<
    string,
    { color: string; background: string; border: string }
> = {
    MaxEUD: {
        color: '#4dd9e8',
        background: 'rgba(77,217,232,0.10)',
        border: 'rgba(77,217,232,0.30)',
    },
    MaxDose: {
        color: '#f5a623',
        background: 'rgba(245,166,35,0.10)',
        border: 'rgba(245,166,35,0.30)',
    },
    MaxDVH: {
        color: '#a78bfa',
        background: 'rgba(167,139,250,0.10)',
        border: 'rgba(167,139,250,0.30)',
    },
};

const DEFAULT_TYPE_STYLE = {
    color: '#94a3b8',
    background: 'rgba(148,163,184,0.10)',
    border: 'rgba(148,163,184,0.30)',
};

function showVolumeForType(type: string): boolean {
    return type !== 'MaxEUD' && type !== 'MaxDose';
}

function showGEUDForType(type: string): boolean {
    return type === 'MaxEUD';
}

const TypeBadge = ({ type }: { type: string }) => {
    const style = TYPE_STYLE[type] ?? DEFAULT_TYPE_STYLE;
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                px: '5px',
                py: '1px',
                borderRadius: '3px',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                lineHeight: 1.5,
                color: style.color,
                backgroundColor: style.background,
                border: `1px solid ${style.border}`,
                whiteSpace: 'nowrap',
            }}
        >
            {type}
        </Box>
    );
};

const EmptyCell = () => (
    <Box component="span" sx={{ color: 'text.disabled', fontSize: '11px' }}>
        —
    </Box>
);

interface OptimizationObjectivesTableProps {
    rows: OptimizationObjectiveRow[];
    structureColorMap: Map<string, string>;
}

const OptimizationObjectivesTable = ({
    rows,
    structureColorMap,
}: OptimizationObjectivesTableProps) => {
    const [order, setOrder] = useState<Order>('asc');

    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
        new Set()
    );

    const handleToggleStructureSort = () => {
        setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    };

    const toggleGroup = (structure: string) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(structure)) {
                next.delete(structure);
            } else {
                next.add(structure);
            }
            return next;
        });
    };

    const groups = useMemo(() => buildGroups(rows, order), [rows, order]);

    const maxDose = useMemo(
        () => rows.reduce((max, r) => (r.dose > max ? r.dose : max), 0),
        [rows]
    );

    return (
        <Paper sx={{ width: '100%', height: '100%', borderRadius: 0 }}>
            <TableContainer
                sx={{
                    maxHeight: '100%',
                    height: '100%',
                    overflow: 'auto',
                    overscrollBehavior: 'contain',
                    ...tableScrollbarStyles,
                }}
            >
                <Table size="small" stickyHeader>
                    <ObjectivesTableHead
                        order={order}
                        onToggleStructureSort={handleToggleStructureSort}
                    />
                    <TableBody>
                        {groups.map((group, groupDisplayIndex) => {
                            const isExpanded = !collapsedGroups.has(
                                group.structure
                            );
                            const roiColor = structureColorMap.get(
                                group.structure
                            );
                            const bandBg =
                                groupDisplayIndex % 2 === 0
                                    ? 'transparent'
                                    : 'rgba(255,255,255,0.02)';

                            return (
                                <Fragment key={group.roiGroupIndex}>
                                    <TableRow
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={isExpanded}
                                        onClick={() =>
                                            toggleGroup(group.structure)
                                        }
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault();
                                                toggleGroup(group.structure);
                                            }
                                        }}
                                        sx={{
                                            backgroundColor: bandBg,
                                            cursor: 'pointer',
                                            opacity: group.visible ? 1 : 0.38,
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                width: 32,
                                                p: 0,
                                                pl: '4px',
                                                borderBottom: 'none',
                                                borderTop: (theme) =>
                                                    `1px solid ${theme.palette.divider}`,
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                tabIndex={-1}
                                                aria-hidden="true"
                                                sx={{
                                                    p: '2px',
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                {isExpanded ? (
                                                    <ExpandLess
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                ) : (
                                                    <ExpandMore
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                )}
                                            </IconButton>
                                        </TableCell>

                                        <TableCell
                                            colSpan={COL_COUNT - 1}
                                            sx={{
                                                borderBottom: 'none',
                                                borderTop: (theme) =>
                                                    `1px solid ${theme.palette.divider}`,
                                                py: '5px',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '7px',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 9,
                                                        height: 9,
                                                        borderRadius: '50%',
                                                        flexShrink: 0,
                                                        backgroundColor:
                                                            roiColor ??
                                                            'rgba(148,163,184,0.4)',
                                                        boxShadow: roiColor
                                                            ? `0 0 5px ${roiColor}90`
                                                            : 'none',
                                                    }}
                                                />
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        letterSpacing: '0.01em',
                                                    }}
                                                >
                                                    {group.structure}
                                                </Box>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: '10px',
                                                        color: 'text.disabled',
                                                        ml: '2px',
                                                    }}
                                                >
                                                    {group.rows.length}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                    </TableRow>

                                    {isExpanded &&
                                        group.rows.map((row) => {
                                            const dosePct =
                                                maxDose > 0
                                                    ? Math.min(
                                                          (row.dose / maxDose) *
                                                              100,
                                                          100
                                                      )
                                                    : 0;
                                            const doseBarColor = row.isControl
                                                ? 'rgba(148,163,184,0.10)'
                                                : 'rgba(77,217,232,0.10)';

                                            const subCellSx = {
                                                py: '4px',
                                                fontSize: '11.5px',
                                                borderBottom: 'none',
                                                opacity: row.visible ? 1 : 0.38,
                                                color: row.isControl
                                                    ? 'text.disabled'
                                                    : 'inherit',
                                            } as const;

                                            return (
                                                <TableRow
                                                    key={row.id}
                                                    sx={{
                                                        backgroundColor: bandBg,
                                                    }}
                                                >
                                                    <TableCell
                                                        sx={{
                                                            ...subCellSx,
                                                            width: 32,
                                                            p: 0,
                                                        }}
                                                    />

                                                    <TableCell sx={subCellSx} />

                                                    <TableCell sx={subCellSx}>
                                                        <TypeBadge
                                                            type={row.type}
                                                        />
                                                    </TableCell>

                                                    <TableCell
                                                        sx={{
                                                            ...subCellSx,
                                                            background: `linear-gradient(to right, ${doseBarColor} ${dosePct}%, transparent ${dosePct}%)`,
                                                            fontVariantNumeric:
                                                                'tabular-nums',
                                                            whiteSpace:
                                                                'nowrap',
                                                            minWidth: 68,
                                                        }}
                                                    >
                                                        <CopyField
                                                            label={row.dose.toFixed(
                                                                1
                                                            )}
                                                            size={12}
                                                        />
                                                    </TableCell>

                                                    <TableCell
                                                        sx={{
                                                            ...subCellSx,
                                                            fontVariantNumeric:
                                                                'tabular-nums',
                                                        }}
                                                    >
                                                        {showVolumeForType(
                                                            row.type
                                                        ) ? (
                                                            <CopyField
                                                                label={row.volumePercent.toFixed(
                                                                    1
                                                                )}
                                                                size={12}
                                                            />
                                                        ) : (
                                                            <EmptyCell />
                                                        )}
                                                    </TableCell>

                                                    <TableCell
                                                        sx={{
                                                            ...subCellSx,
                                                            fontVariantNumeric:
                                                                'tabular-nums',
                                                        }}
                                                    >
                                                        {showGEUDForType(
                                                            row.type
                                                        ) ? (
                                                            <CopyField
                                                                label={row.gEUD.toFixed(
                                                                    1
                                                                )}
                                                                size={12}
                                                            />
                                                        ) : (
                                                            <EmptyCell />
                                                        )}
                                                    </TableCell>

                                                    <TableCell
                                                        sx={{
                                                            ...subCellSx,
                                                            fontVariantNumeric:
                                                                'tabular-nums',
                                                        }}
                                                    >
                                                        <CopyField
                                                            label={String(
                                                                row.priority
                                                            )}
                                                            size={12}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default OptimizationObjectivesTable;
